import moment from "moment-timezone";
import ClassModel, { isClass } from "src/models/ClassModel";
import MembershipOfferingModel, {
  isMembershipOffering,
} from "src/models/MembershipOfferingModel";
import OfferingModel from "src/models/OfferingModel";
import { dateFormat, dateFormatLong } from "src/utils/constants";
import { OfferingStatus } from "types/code-generator";

interface GetOfferingDatesInput {
  offering: OfferingModel | ClassModel | MembershipOfferingModel;
  timezone: string;
}

export default ({ offering, timezone = "" }: GetOfferingDatesInput) => {
  const today = moment();
  let publishString = "Viewable to Public";
  let registrationString = "Registration Open";

  let published = true;
  let registrationOpen = true;
  let publishedSoon = false;
  let registrationOpenSoon = false;

  let publishDate;
  let unpublishDate;
  let registrationOpenDate;
  let registrationCloseDate;

  const requiresRegistration =
    isClass(offering) && offering.requiresRegistration;

  if (isClass(offering) && !requiresRegistration) {
    publishString = "Viewable to Eligible Participants";
  }

  if (offering.publishDate || offering.unpublishDate) {
    publishDate = offering.publishDate && moment(offering.publishDate);
    unpublishDate = offering.unpublishDate && moment(offering.unpublishDate);

    if (publishDate?.isAfter(today)) {
      published = false;
      publishedSoon = true;
      publishString = `Viewable ${publishDate.format(
        dateFormatLong
      )} ${timezone}`;
    }

    if (unpublishDate?.isBefore(today)) {
      published = false;
      publishString = "Not Viewable to Public";

      if (!requiresRegistration) {
        publishString = "Not Viewable to Eligible Participants";
      }
    }
  }

  if (offering.registrationOpen || offering.registrationClose) {
    registrationOpenDate =
      offering.registrationOpen && moment(offering.registrationOpen);
    registrationCloseDate =
      offering.registrationClose && moment(offering.registrationClose);

    if (registrationOpenDate) {
      if (registrationOpenDate.isAfter(today, "minute")) {
        registrationOpen = false;
        registrationOpenSoon = true;
        registrationString = `Registration Opens ${registrationOpenDate.format(
          dateFormatLong
        )} ${timezone}`;
      } else if (registrationOpenDate.isSameOrBefore(today, "minute")) {
        registrationString = `Registration Open`;
      } else if (registrationCloseDate) {
        registrationString = `Registration Open: ${registrationOpenDate.format(
          dateFormat
        )}–${registrationCloseDate.format(dateFormat)}`;
      } else {
        registrationOpenSoon = true;
        registrationString = `Registration Open: ${registrationOpenDate.format(
          dateFormat
        )}`;
      }
    } else if (registrationCloseDate) {
      registrationString = `Registration Open Thru: ${registrationCloseDate.format(
        dateFormatLong
      )} ${timezone}`;
    }
  }

  if (
    (!offering.registrationClose && !offering.registrationOpen) ||
    (registrationCloseDate && today.isAfter(registrationCloseDate, "day")) ||
    !offering.registrationOpen ||
    (!registrationCloseDate &&
      offering.endDate &&
      today.isAfter(offering.endDate, "day"))
  ) {
    registrationString = "Registration Closed";
    registrationOpen = false;
  }

  if (!offering.publishDate) {
    publishString = "Not Viewable to Public";
    published = false;

    if (!requiresRegistration) {
      publishString = "Not Viewable to Eligible Participants";
    }
  }

  if (
    !isMembershipOffering(offering) &&
    publishDate &&
    today.isSameOrBefore(publishDate) &&
    unpublishDate &&
    today.isSameOrAfter(unpublishDate)
  ) {
    publishString = "Not Viewable to Public";
    registrationString = "Not Open For Registration";
    published = false;
    registrationOpen = false;
  }

  if (offering.status === OfferingStatus.Archived) {
    publishString = "Archived";
    registrationString = "Not Viewable to Public";
    published = false;
    registrationOpen = false;
  }

  if (isClass(offering) && !requiresRegistration) {
    registrationString = "Registration Not Required";
    registrationOpen = true;
  }

  return {
    publishString,
    registrationString,
    published,
    publishedSoon,
    registrationOpen,
    registrationOpenSoon,
  };
};
